import React, { useState, useEffect, useMemo, useRef } from 'react';
import axiosInstance from 'shared/services/api/axiosInstance';
import NotificationPreferences from 'features/settings/components/panels/NotificationPreferences';
import { useTeam } from 'shared/context/teamContext';
import { Monitor, Sun, Moon } from 'lucide-react';
import SegmentedTabs from 'shared/components/ui/SegmentedTabs';
import { useTheme } from 'shared/theme/ThemeContext';

import BrandDropdown from 'shared/components/ui/BrandDropdown';
import Avatar from 'shared/components/ui/Avatar';
import { PageLoader } from 'shared/components/ui/BrandLoader';
import { Section, Row, textBtn, neutralBtn, primaryBtn, inputCls, rowValue } from 'features/settings/components/SettingsRows';

const THEME_OPTIONS = [
  { value: 'system', label: 'System', hint: 'Follow the system setting', icon: ({ size }) => <Monitor size={size} aria-hidden /> },
  { value: 'light', label: 'Light', hint: 'Light theme', icon: ({ size }) => <Sun size={size} aria-hidden /> },
  { value: 'dark', label: 'Dark', hint: 'Dark theme', icon: ({ size }) => <Moon size={size} aria-hidden /> },
];

const textareaCls = `${inputCls} h-auto min-h-[72px] py-1.5 resize-y`;

const PREFERENCE_PLACEHOLDER_ROTATION = [
  'e.g. Keep explanations brief and to the point',
  'e.g. Ask clarifying questions before giving a response',
  'e.g. Prefer short bullet answers when possible',
  'e.g. Use plain language: avoid jargon',
  'e.g. Call out assumptions and note uncertainty clearly',
];

const WORK_FUNCTION_OPTIONS = [
  'Product Manager',
  'Developer',
  'Founders',
  'Marketing',
  'Sales',
  'Data Science',
  'Leadership',
  'Other'
];

const WORK_FUNCTION_DROPDOWN_OPTIONS = WORK_FUNCTION_OPTIONS.map((o) => ({ value: o, label: o }));

function normalizePreference(value) {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    return value.filter((item) => typeof item === 'string').join('\n');
  }
  return '';
}

function workStateFromApi(workFunction) {
  if (!workFunction || typeof workFunction !== 'string') {
    return { role: '', other: '' };
  }
  const preset = WORK_FUNCTION_OPTIONS.filter((o) => o !== 'Other');
  if (preset.includes(workFunction)) {
    return { role: workFunction, other: '' };
  }
  return { role: 'Other', other: workFunction };
}

/**
 * User account + notification preferences for Settings → Personal → Profile.
 */
const UserSettingsGeneralPanel = () => {
  const { selectedTeam } = useTeam();
  const { mode: themeMode, setMode: setThemeMode } = useTheme();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [name, setName] = useState('');
  const [workRole, setWorkRole] = useState('');
  const [workOther, setWorkOther] = useState('');
  const [preference, setPreference] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [prefPlaceholderIdx, setPrefPlaceholderIdx] = useState(0);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  const fileRef = useRef(null);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordRequestLoading, setPasswordRequestLoading] = useState(false);
  const [passwordRequestError, setPasswordRequestError] = useState('');
  const [otpSent, setOtpSent] = useState(false);

  const [otp, setOtp] = useState('');
  const [passwordVerifyLoading, setPasswordVerifyLoading] = useState(false);
  const [passwordVerifyError, setPasswordVerifyError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  const [teamPrefsMessage, setTeamPrefsMessage] = useState('');
  const [teamPrefsError, setTeamPrefsError] = useState('');
  const [setDefaultSaving, setSetDefaultSaving] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get('/auth/me');
        const userData = response.data.user;
        setUser(userData);
        setName(userData.name || '');
        const ws = workStateFromApi(userData.workFunction);
        setWorkRole(ws.role);
        setWorkOther(ws.other);
        setPreference(normalizePreference(userData.preference));
        setAvatarUrl(userData.avatar_url || null);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load user profile');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  useEffect(() => {
    if (normalizePreference(preference).trim().length > 0) return undefined;
    const id = setInterval(() => {
      setPrefPlaceholderIdx((i) => (i + 1) % PREFERENCE_PLACEHOLDER_ROTATION.length);
    }, 4500);
    return () => clearInterval(id);
  }, [preference]);

  const [editing, setEditing] = useState(null);

  /** One save path for every inline edit; same endpoint and payload as before. */
  const saveProfile = async (patch = {}) => {
    const nextName = (patch.name ?? name).trim();
    const nextPref = patch.preference ?? preference;
    const role = patch.workRole ?? workRole;
    const other = patch.workOther ?? workOther;
    setProfileError('');
    if (!nextName) {
      setProfileError('Name is required');
      return;
    }
    if (role === 'Other' && !other.trim()) {
      setProfileError('Describe your work');
      return;
    }
    const resolvedWork = role === 'Other' ? other.trim() : role.trim() || null;
    setProfileSaving(true);
    try {
      await axiosInstance.put('/auth/update-profile', {
        name: nextName,
        workFunction: resolvedWork,
        preference: nextPref,
      });
      setUser((prev) =>
        prev ? { ...prev, name: nextName, workFunction: resolvedWork, preference: nextPref } : null
      );
      setName(nextName);
      setWorkRole(role);
      setWorkOther(other);
      setPreference(nextPref);
      setEditing(null);
      try {
        const storedUser = localStorage.getItem('User');
        if (storedUser) {
          const parsed = JSON.parse(storedUser);
          parsed.name = nextName;
          localStorage.setItem('User', JSON.stringify(parsed));
        }
      } catch (_) {}
    } catch (err) {
      setProfileError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const cancelEdit = () => {
    setName(user?.name || '');
    const ws = workStateFromApi(user?.workFunction);
    setWorkRole(ws.role);
    setWorkOther(ws.other);
    setPreference(normalizePreference(user?.preference));
    setProfileError('');
    setEditing(null);
  };

  const handlePasswordRequest = async (e) => {
    e.preventDefault();
    setPasswordRequestLoading(true);
    setPasswordRequestError('');

    if (newPassword !== confirmPassword) {
      setPasswordRequestError('New password and confirmation do not match');
      setPasswordRequestLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setPasswordRequestError('Password must be at least 8 characters long');
      setPasswordRequestLoading(false);
      return;
    }

    try {
      await axiosInstance.post('/auth/change-password-request', {
        currentPassword,
        newPassword,
        confirmPassword
      });
      setOtpSent(true);
    } catch (err) {
      setPasswordRequestError(err.response?.data?.message || 'Failed to send verification code');
    } finally {
      setPasswordRequestLoading(false);
    }
  };

  const handlePasswordVerify = async (e) => {
    e.preventDefault();
    setPasswordVerifyLoading(true);
    setPasswordVerifyError('');
    setPasswordSuccess('');

    try {
      await axiosInstance.post('/auth/change-password-verify', {
        otp: otp.trim(),
        newPassword
      });
      setPasswordSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setOtp('');
      setOtpSent(false);
    } catch (err) {
      setPasswordVerifyError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setPasswordVerifyLoading(false);
    }
  };

  const handleResetPasswordFlow = () => {
    setOtpSent(false);
    setOtp('');
    setPasswordRequestError('');
    setPasswordVerifyError('');
    setPasswordSuccess('');
  };

  const currentTeamId = selectedTeam?.id ?? selectedTeam?._id ?? null;
  const isCurrentTeamDefault = useMemo(() => {
    if (user?.defaultTeamId == null || currentTeamId == null) return false;
    return String(user.defaultTeamId) === String(currentTeamId);
  }, [user?.defaultTeamId, currentTeamId]);

  const handleSetDefaultProject = async () => {
    if (!currentTeamId) return;
    setSetDefaultSaving(true);
    setTeamPrefsError('');
    setTeamPrefsMessage('');
    try {
      await axiosInstance.post('/team/user/default-team', { teamId: currentTeamId });
      const me = await axiosInstance.get('/auth/me');
      setUser(me.data.user);
      setTeamPrefsMessage('This project is now your default.');
      setTimeout(() => setTeamPrefsMessage(''), 2500);
    } catch (err) {
      setTeamPrefsError(err.response?.data?.message || 'Could not set default project');
    } finally {
      setSetDefaultSaving(false);
    }
  };


  if (loading) return <PageLoader label="Loading profile" />;

  if (error || !user) {
    return <div className="text-[13px] text-danger">{error || 'User not found'}</div>;
  }

  /**
   * Your picture.
   *
   * Signing in with Google already handed us a photo — it has been sitting
   * unread in the account record since the day you signed up, so people who
   * had a picture still appeared as two letters. It shows now, and this
   * replaces it with one of your own. "Remove" clears yours and falls back to
   * the Google photo, or to initials if there was never one.
   */
  const pickAvatar = () => { setAvatarError(''); fileRef.current?.click(); };

  const onAvatarChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setAvatarBusy(true);
    setAvatarError('');
    try {
      const form = new FormData();
      form.append('avatar', file);
      const { data } = await axiosInstance.post('/auth/avatar', form);
      setAvatarUrl(data?.avatar_url || null);
    } catch (err) {
      setAvatarError(err?.response?.data?.error || 'Could not save that image');
    } finally {
      setAvatarBusy(false);
    }
  };

  const removeAvatar = async () => {
    setAvatarBusy(true);
    setAvatarError('');
    try {
      const { data } = await axiosInstance.delete('/auth/avatar');
      setAvatarUrl(data?.avatar_url || null);
    } catch (err) {
      setAvatarError(err?.response?.data?.error || 'Could not remove the image');
    } finally {
      setAvatarBusy(false);
    }
  };

  const isEmailUser = user.isEmailUser === true || user.auth_method === 'email';
  const workLabel = user.workFunction || '';
  const err = (m) => (m ? <span className="text-[12px] text-danger">{m}</span> : null);

  return (
    <div className="w-full pb-8 text-left" style={{ fontFamily: 'Inter, sans-serif' }}>
      <Section title="Profile">
        <Row label="Photo">
          <Avatar name={name || user.name} src={avatarUrl} size={28} />
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={onAvatarChosen}
            className="hidden"
          />
          <button type="button" onClick={pickAvatar} disabled={avatarBusy} className={textBtn}>
            {avatarBusy ? 'Saving' : avatarUrl ? 'Change' : 'Add'}
          </button>
          {avatarUrl ? (
            <button type="button" onClick={removeAvatar} disabled={avatarBusy} className={textBtn}>
              Remove
            </button>
          ) : null}
          {err(avatarError)}
        </Row>

        <Row label="Name">
          {editing === 'name' ? (
            <form
              className="flex w-full max-w-sm items-center gap-2"
              onSubmit={(e) => { e.preventDefault(); saveProfile(); }}
            >
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={inputCls}
                placeholder="Your name"
              />
              <button type="submit" disabled={profileSaving} className={primaryBtn}>Save</button>
              <button type="button" onClick={cancelEdit} className={neutralBtn}>Cancel</button>
            </form>
          ) : (
            <>
              <span className={`${rowValue} truncate`}>{user.name}</span>
              <button type="button" onClick={() => setEditing('name')} className={textBtn}>Edit</button>
            </>
          )}
        </Row>

        <Row label="Email">
          <span className={`${rowValue} truncate`}>{user.email}</span>
        </Row>

        <Row label="Work">
          {editing === 'work' || workRole === 'Other' ? (
            <div className="flex w-full max-w-sm flex-col gap-2">
              {workRole === 'Other' ? (
                <form
                  className="flex items-center gap-2"
                  onSubmit={(e) => { e.preventDefault(); saveProfile(); }}
                >
                  <input
                    autoFocus
                    value={workOther}
                    onChange={(e) => setWorkOther(e.target.value)}
                    className={inputCls}
                    placeholder="Describe your work"
                  />
                  <button type="submit" disabled={profileSaving} className={primaryBtn}>Save</button>
                  <button type="button" onClick={cancelEdit} className={neutralBtn}>Cancel</button>
                </form>
              ) : (
                <BrandDropdown
                  value={workRole}
                  onChange={(v) => {
                    setWorkRole(v);
                    setWorkOther('');
                    if (v !== 'Other') saveProfile({ workRole: v, workOther: '' });
                  }}
                  options={WORK_FUNCTION_DROPDOWN_OPTIONS}
                  placeholder="Select"
                  ariaLabel="Your work function"
                />
              )}
            </div>
          ) : (
            <>
              <span className={`${rowValue} truncate ${workLabel ? '' : 'text-[color:var(--text-muted)]'}`}>
                {workLabel || 'Not set'}
              </span>
              <button type="button" onClick={() => setEditing('work')} className={textBtn}>Edit</button>
            </>
          )}
        </Row>

        <Row label="Response preferences" align="start">
          {editing === 'pref' ? (
            <div className="flex w-full max-w-md flex-col gap-2">
              <textarea
                autoFocus
                value={preference}
                onChange={(e) => setPreference(e.target.value)}
                className={textareaCls}
                placeholder={PREFERENCE_PLACEHOLDER_ROTATION[prefPlaceholderIdx]}
                rows={3}
              />
              <div className="flex gap-2">
                <button type="button" onClick={() => saveProfile()} disabled={profileSaving} className={primaryBtn}>Save</button>
                <button type="button" onClick={cancelEdit} className={neutralBtn}>Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <span className={`${rowValue} line-clamp-3 whitespace-pre-wrap ${normalizePreference(user.preference) ? '' : 'text-[color:var(--text-muted)]'}`}>
                {normalizePreference(user.preference) || 'Not set'}
              </span>
              <button type="button" onClick={() => setEditing('pref')} className={textBtn}>Edit</button>
            </>
          )}
        </Row>
        {profileError ? <div className="pt-2">{err(profileError)}</div> : null}
      </Section>

      <Section title="Theme">
        <Row label="Appearance">
          <SegmentedTabs
            options={THEME_OPTIONS}
            value={themeMode}
            onChange={setThemeMode}
            ariaLabel="Theme"
          />
        </Row>
      </Section>

      {isEmailUser ? (
        <Section title="Password">
          {!otpSent ? (
            <form onSubmit={handlePasswordRequest}>
              {[
                ['Current', currentPassword, setCurrentPassword],
                ['New', newPassword, setNewPassword],
                ['Confirm', confirmPassword, setConfirmPassword],
              ].map(([label, val, set]) => (
                <Row key={label} label={label}>
                  <input
                    type="password"
                    value={val}
                    onChange={(e) => set(e.target.value)}
                    className={`${inputCls} max-w-sm`}
                    required
                    minLength={label === 'Current' ? undefined : 8}
                  />
                </Row>
              ))}
              <div className="flex items-center gap-3 pt-3">
                <button type="submit" disabled={passwordRequestLoading} className={neutralBtn}>
                  {passwordRequestLoading ? 'Sending' : 'Change password'}
                </button>
                {err(passwordRequestError)}
                {passwordSuccess ? <span className="text-[12px] text-success">{passwordSuccess}</span> : null}
              </div>
            </form>
          ) : (
            <form onSubmit={handlePasswordVerify}>
              <Row label="Code">
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className={`${inputCls} max-w-[10rem]`}
                  placeholder="6 digits"
                  maxLength={6}
                  required
                />
                <span className="truncate text-[12px] text-[color:var(--text-muted)]">Sent to {user.email}</span>
              </Row>
              <div className="flex items-center gap-2 pt-3">
                <button type="submit" disabled={passwordVerifyLoading} className={primaryBtn}>
                  {passwordVerifyLoading ? 'Verifying' : 'Verify'}
                </button>
                <button type="button" onClick={handleResetPasswordFlow} className={neutralBtn}>Cancel</button>
                {err(passwordVerifyError)}
              </div>
            </form>
          )}
        </Section>
      ) : null}

      <NotificationPreferences />

      {currentTeamId ? (
        <Section title="Default project">
          <Row label="Opens on sign in">
            <span className={`${rowValue} truncate`}>
              {isCurrentTeamDefault ? selectedTeam?.name || 'This project' : ''}
            </span>
            <button
              type="button"
              onClick={handleSetDefaultProject}
              disabled={setDefaultSaving || isCurrentTeamDefault}
              className={neutralBtn}
            >
              {isCurrentTeamDefault ? 'Default' : setDefaultSaving ? 'Saving' : 'Make default'}
            </button>
          </Row>
          {teamPrefsMessage ? <div className="pt-2 text-[12px] text-success">{teamPrefsMessage}</div> : null}
          {err(teamPrefsError)}
        </Section>
      ) : null}
    </div>
  );
};

export default UserSettingsGeneralPanel;
